import { z } from 'zod';

/** Bumped whenever the wire format changes in a breaking way. */
export const PROTOCOL_VERSION = 2;

export const CODE_LENGTH = 6;

/** Fallback page count used before any presenter puts a deck on screen. */
export const DEMO_TOTAL_PAGES = 8;

export const RoomCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'room code must be exactly 6 digits');

export const ClientIdSchema = z.string().min(1).max(64);

/** Roles on the wire.
 *  - screen: the TV / projector. Display only, owns the room record.
 *  - owner:  the first phone to scan. Controls who is on screen.
 *  - presenter: a phone allowed to project its own deck.
 *  - viewer: audience. Can ask questions, never appears on screen.
 */
export const RoleSchema = z.enum(['screen', 'owner', 'presenter', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const LayoutSchema = z.union([z.literal(1), z.literal(2), z.literal(4)]);
export type Layout = z.infer<typeof LayoutSchema>;

/** Page size in PDF points (1/72 inch). */
export const DeckPageSchema = z.object({
  w: z.number().positive().max(20_000),
  h: z.number().positive().max(20_000),
});

export const DeckMetaSchema = z
  .object({
    id: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
    name: z.string().min(1).max(200),
    total: z.number().int().min(1).max(1000),
    pages: z.array(DeckPageSchema).min(1).max(1000),
    native: z.boolean().optional(),
    notes: z.array(z.string()).optional(),
  })
  .refine((deck) => deck.pages.length === deck.total, {
    message: 'pages must match deck total',
    path: ['pages'],
  });

export type DeckMeta = z.infer<typeof DeckMetaSchema>;
export type DeckPage = z.infer<typeof DeckPageSchema>;

/** Lightweight deck info safe to broadcast on every room update. */
export const DeckSummarySchema = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  name: z.string().min(1).max(200),
  total: z.number().int().min(1).max(1000),
});
export type DeckSummary = z.infer<typeof DeckSummarySchema>;

export const PresenterSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  online: z.boolean(),
  owner: z.boolean(),
  page: z.number().int().min(1),
  total: z.number().int().min(1),
  version: z.number().int().min(0),
  deck: DeckSummarySchema.nullable(),
  /** True when this presenter is streaming frames from their own laptop. */
  live: z.boolean(),
  /** True when the live stream is a captured window, not a PDF page. */
  capture: z.boolean().default(false),
  /** True when the presenter asked the owner to put them on screen. */
  requested: z.boolean(),
});

export type Presenter = z.infer<typeof PresenterSchema>;

export const RoomViewSchema = z.object({
  code: RoomCodeSchema,
  layout: LayoutSchema,
  /** Presenter ids shown on screen, in pane order. At most 4. */
  activeIds: z.array(z.string()).max(4),
  presenters: z.array(PresenterSchema).max(16),
  locked: z.boolean(),
  ownerName: z.string().nullable(),
  ownerOnline: z.boolean(),
  screenOnline: z.boolean(),
  /** True while the big screen should show the invite code overlay. */
  inviteVisible: z.boolean(),
  viewers: z.number().int().min(0),
  version: z.number().int().min(0),
});

export type RoomView = z.infer<typeof RoomViewSchema>;

const base = { v: z.literal(PROTOCOL_VERSION) };

export const CreateMessageSchema = z.object({
  ...base,
  type: z.literal('create'),
  clientId: ClientIdSchema,
});

export const JoinMessageSchema = z.object({
  ...base,
  type: z.literal('join'),
  code: RoomCodeSchema,
  role: RoleSchema,
  clientId: ClientIdSchema,
  /** Screen reconnection secret. */
  screenToken: z.string().min(16).max(200).optional(),
  /** Presenter reconnection secret. */
  presenterToken: z.string().min(16).max(200).optional(),
  /** Owner reconnection secret. */
  ownerToken: z.string().min(16).max(200).optional(),
  name: z.string().max(64).optional(),
  /** UI language, used for server-generated default names. */
  lang: z.enum(['zh', 'en']).optional(),
});

export const NavMessageSchema = z.object({
  ...base,
  type: z.literal('nav'),
  action: z.enum(['next', 'prev', 'goto']),
  page: z.number().int().min(1).max(9999).optional(),
});

export const LaserMessageSchema = z.object({
  ...base,
  type: z.literal('laser'),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  active: z.boolean(),
});

export const CalibrateMessageSchema = z.object({
  ...base,
  type: z.literal('calibrate'),
  corners: z.array(
    z.object({
      x: z.number().min(0).max(1),
      y: z.number().min(0).max(1),
    }),
  ).length(4),
});

export const PingMessageSchema = z.object({
  ...base,
  type: z.literal('ping'),
  t: z.number(),
});

export const LeaveMessageSchema = z.object({
  ...base,
  type: z.literal('leave'),
});

export const PresenterStateMessageSchema = z.object({
  ...base,
  type: z.literal('presenter-state'),
  currentPage: z.number().int().min(1),
  totalPages: z.number().int().min(1),
  currentNotes: z.string().optional(),
  nextPageNotes: z.string().optional(),
  elapsedMs: z.number().int().min(0),
  timerRunning: z.boolean(),
});

export const PresenterControlMessageSchema = z.object({
  ...base,
  type: z.literal('presenter-control'),
  action: z.enum(['start-timer', 'pause-timer', 'reset-timer']),
});

export const ViewerQuestionMessageSchema = z.object({
  ...base,
  type: z.literal('viewer-question'),
  text: z.string().min(1).max(1000),
  viewerName: z.string().max(64).optional(),
});

export const FrameMessageSchema = z.object({
  ...base,
  type: z.literal('frame'),
  page: z.number().int().min(1).max(1000),
  total: z.number().int().min(1).max(1000),
  width: z.number().int().min(1).max(4096),
  height: z.number().int().min(1).max(4096),
  /** Data URL (image/jpeg or image/webp), already resized by the client. */
  image: z.string().min(32).max(4_000_000),
  /** Optional small thumbnail for the host drawer. */
  thumb: z.string().max(200_000).optional(),
  /** True when this frame is a captured window, not a PDF page. */
  capture: z.boolean().optional(),
});

export const FrameClearMessageSchema = z.object({
  ...base,
  type: z.literal('frame-clear'),
});

export const ResetRoomMessageSchema = z.object({
  ...base,
  type: z.literal('reset-room'),
});

export const SyncRequestMessageSchema = z.object({
  ...base,
  type: z.literal('sync-request'),
});

export const HostCommandMessageSchema = z.object({
  ...base,
  type: z.literal('host-command'),
  action: z.enum(['toggle-hud', 'toggle-fullscreen', 'show-pair', 'hide-pair']),
});

export const RequestScreenMessageSchema = z.object({
  ...base,
  type: z.literal('request-screen'),
});

export const ShowPresenterMessageSchema = z.object({
  ...base,
  type: z.literal('show'),
  presenterId: z.string().min(1).max(64).nullable(),
});

export const SetActiveMessageSchema = z.object({
  ...base,
  type: z.literal('set-active'),
  ids: z.array(z.string().min(1).max(64)).max(4),
});

export const SetLayoutMessageSchema = z.object({
  ...base,
  type: z.literal('set-layout'),
  layout: LayoutSchema,
});

export const KickPresenterMessageSchema = z.object({
  ...base,
  type: z.literal('kick'),
  presenterId: z.string().min(1).max(64),
});

export const LockRoomMessageSchema = z.object({
  ...base,
  type: z.literal('lock'),
  locked: z.boolean(),
});

export const ApprovePresenterMessageSchema = z.object({
  ...base,
  type: z.literal('approve'),
  presenterId: z.string().min(1).max(64),
});

export const DenyPresenterMessageSchema = z.object({
  ...base,
  type: z.literal('deny'),
  presenterId: z.string().min(1).max(64),
});

export const ClientMessageSchema = z
  .discriminatedUnion('type', [
    CreateMessageSchema,
    JoinMessageSchema,
    NavMessageSchema,
    LaserMessageSchema,
    PingMessageSchema,
    LeaveMessageSchema,
    CalibrateMessageSchema,
    PresenterControlMessageSchema,
    ViewerQuestionMessageSchema,
    FrameMessageSchema,
    FrameClearMessageSchema,
    ResetRoomMessageSchema,
    SyncRequestMessageSchema,
    HostCommandMessageSchema,
    RequestScreenMessageSchema,
    ShowPresenterMessageSchema,
    SetActiveMessageSchema,
    SetLayoutMessageSchema,
    KickPresenterMessageSchema,
    LockRoomMessageSchema,
    ApprovePresenterMessageSchema,
    DenyPresenterMessageSchema,
  ])
  .superRefine((message, ctx) => {
    if (message.type === 'nav' && message.action === 'goto' && typeof message.page !== 'number') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'goto requires a page number',
        path: ['page'],
      });
    }
    if (message.type === 'calibrate' && message.corners.length !== 4) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'calibrate requires exactly 4 corners',
        path: ['corners'],
      });
    }
  });

export type ClientMessage = z.infer<typeof ClientMessageSchema>;

export const ErrorCodeSchema = z.enum([
  'room_not_found',
  'invalid_token',
  'room_full',
  'room_locked',
  'not_owner',
  'presenter_not_found',
  'frame_too_large',
  'rate_limited',
  'too_many_attempts',
  'bad_message',
  'server_busy',
  'room_expired',
  'unsupported_version',
  'internal',
  'viewer_name_taken',
]);

export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

const serverBase = { v: z.literal(PROTOCOL_VERSION) };

export const CalibrationResultSchema = z.object({
  ...serverBase,
  type: z.literal('calibration-result'),
  matrix: z.array(z.number()).length(9),
});

export const CreatedMessageSchema = z.object({
  ...serverBase,
  type: z.literal('created'),
  code: RoomCodeSchema,
  screenToken: z.string(),
  room: RoomViewSchema,
});

export const JoinedMessageSchema = z.object({
  ...serverBase,
  type: z.literal('joined'),
  role: RoleSchema,
  code: RoomCodeSchema,
  room: RoomViewSchema,
  /** Presenter identity for owner/presenter connections. */
  presenterId: z.string().optional(),
  presenterToken: z.string().optional(),
  /** Only sent to the owner: authorises owner commands on reconnect. */
  ownerToken: z.string().optional(),
});

export const RoomStateMessageSchema = z.object({
  ...serverBase,
  type: z.literal('room-state'),
  room: RoomViewSchema,
});

export const LaserBroadcastSchema = z.object({
  ...serverBase,
  type: z.literal('laser'),
  presenterId: z.string(),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  active: z.boolean(),
});

export const FrameServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('frame'),
  presenterId: z.string(),
  page: z.number().int().min(1),
  total: z.number().int().min(1),
  width: z.number().int().min(1),
  height: z.number().int().min(1),
  image: z.string(),
  thumb: z.string().optional(),
  capture: z.boolean().optional(),
});

export const HostChangedMessageSchema = z.object({
  ...serverBase,
  type: z.literal('host-changed'),
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  /** Only sent to the new owner connection. */
  ownerToken: z.string().optional(),
});

export const PongMessageSchema = z.object({
  ...serverBase,
  type: z.literal('pong'),
  t: z.number(),
});

export const ErrorMessageSchema = z.object({
  ...serverBase,
  type: z.literal('error'),
  code: ErrorCodeSchema,
  message: z.string(),
  fatal: z.boolean().optional(),
});

export const ShutdownMessageSchema = z.object({
  ...serverBase,
  type: z.literal('shutdown'),
  retryAfterMs: z.number().int().min(0),
});

export const KickedMessageSchema = z.object({
  ...serverBase,
  type: z.literal('kicked'),
  reason: z.string(),
});

export const CalibrationResultMessageSchema = z.object({
  ...serverBase,
  type: z.literal('calibration-result'),
  matrix: z.array(z.number()).length(9),
});

export const PresenterStateServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('presenter-state'),
  presenterId: z.string(),
  currentPage: z.number().int().min(1),
  totalPages: z.number().int().min(1),
  currentNotes: z.string().optional(),
  nextPageNotes: z.string().optional(),
  elapsedMs: z.number().int().min(0),
  timerRunning: z.boolean(),
});

export const ViewerJoinedServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('viewer-joined'),
  viewerId: z.string(),
  viewerName: z.string().optional(),
  totalViewers: z.number().int().min(0),
});

export const ViewerLeftServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('viewer-left'),
  viewerId: z.string(),
  totalViewers: z.number().int().min(0),
});

export const ViewerQuestionServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('viewer-question'),
  questionId: z.string(),
  text: z.string(),
  viewerName: z.string().optional(),
  timestamp: z.number().int().min(0),
});

export const HostCommandServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('host-command'),
  action: z.enum(['toggle-hud', 'toggle-fullscreen', 'show-pair', 'hide-pair']),
});

export const SyncResponseServerMessageSchema = z.object({
  ...serverBase,
  type: z.literal('sync-response'),
  room: RoomViewSchema,
});

export const ServerMessageSchema = z.discriminatedUnion('type', [
  CreatedMessageSchema,
  JoinedMessageSchema,
  RoomStateMessageSchema,
  LaserBroadcastSchema,
  FrameServerMessageSchema,
  HostChangedMessageSchema,
  PongMessageSchema,
  ErrorMessageSchema,
  ShutdownMessageSchema,
  KickedMessageSchema,
  CalibrationResultMessageSchema,
  PresenterStateServerMessageSchema,
  ViewerJoinedServerMessageSchema,
  ViewerLeftServerMessageSchema,
  ViewerQuestionServerMessageSchema,
  HostCommandServerMessageSchema,
  SyncResponseServerMessageSchema,
]);

export type ServerMessage = z.infer<typeof ServerMessageSchema>;

export type ParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

function firstIssue(error: z.ZodError): string {
  const issue = error.issues[0];
  if (!issue) return 'invalid payload';
  const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
  return `${path}${issue.message}`;
}

export function parseClientMessage(raw: string): ParseResult<ClientMessage> {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'payload is not valid JSON' };
  }
  const result = ClientMessageSchema.safeParse(data);
  if (!result.success) return { ok: false, error: firstIssue(result.error) };
  return { ok: true, value: result.data };
}

export function parseServerMessage(raw: string): ParseResult<ServerMessage> {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'payload is not valid JSON' };
  }
  const result = ServerMessageSchema.safeParse(data);
  if (!result.success) return { ok: false, error: firstIssue(result.error) };
  return { ok: true, value: result.data };
}

export function errorMessage(code: ErrorCode, message: string, fatal = false): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'error', code, message, fatal };
}

export function roomStateMessage(room: RoomView): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'room-state', room };
}

export function calibrationResultMessage(matrix: number[]): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'calibration-result', matrix };
}

export function presenterStateMessage(
  presenterId: string,
  currentPage: number,
  totalPages: number,
  currentNotes: string | undefined,
  nextPageNotes: string | undefined,
  elapsedMs: number,
  timerRunning: boolean,
): ServerMessage {
  return {
    v: PROTOCOL_VERSION,
    type: 'presenter-state',
    presenterId,
    currentPage,
    totalPages,
    currentNotes,
    nextPageNotes,
    elapsedMs,
    timerRunning,
  };
}

export function viewerJoinedMessage(
  viewerId: string,
  viewerName: string | undefined,
  totalViewers: number,
): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'viewer-joined', viewerId, viewerName, totalViewers };
}

export function viewerLeftMessage(viewerId: string, totalViewers: number): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'viewer-left', viewerId, totalViewers };
}

export function viewerQuestionMessage(
  questionId: string,
  text: string,
  viewerName: string | undefined,
  timestamp: number,
): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'viewer-question', questionId, text, viewerName, timestamp };
}

export function syncResponseMessage(room: RoomView): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'sync-response', room };
}

export function frameMessage(
  presenterId: string,
  page: number,
  total: number,
  width: number,
  height: number,
  image: string,
  thumb?: string,
  capture?: boolean,
): ServerMessage {
  return {
    v: PROTOCOL_VERSION,
    type: 'frame',
    presenterId,
    page,
    total,
    width,
    height,
    image,
    thumb,
    capture,
  };
}

export function hostChangedMessage(
  ownerId: string | null,
  ownerName: string | null,
  ownerToken?: string,
): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'host-changed', ownerId, ownerName, ownerToken };
}

export function hostCommandMessage(
  action: 'toggle-hud' | 'toggle-fullscreen' | 'show-pair' | 'hide-pair',
): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'host-command', action };
}

export function kickedMessage(reason: string): ServerMessage {
  return { v: PROTOCOL_VERSION, type: 'kicked', reason };
}
